import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const THREADS_VIEWPORT_ANCHOR_ID = 'threads-viewport-anchor';

const THREADS_IFRAME_ID = 'az-threads-iframe';

export const THREADS_SRC = 'https://azmc.us/auth';

export const ThreadsIframeManager: React.FC = () => {
  const location = useLocation();
  const rafRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    let iframe = document.getElementById(THREADS_IFRAME_ID) as HTMLIFrameElement | null;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.allow = 'fullscreen; clipboard-write';
      iframe.allowFullscreen = true;
      iframe.id = THREADS_IFRAME_ID;
      iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin');
      iframe.src = THREADS_SRC;
      iframe.style.border = '0';
      iframe.style.borderRadius = '0.5rem';
      iframe.style.height = '0px';
      iframe.style.left = '0px';
      iframe.style.pointerEvents = 'none';
      iframe.style.position = 'fixed';
      iframe.style.top = '0px';
      iframe.style.visibility = 'hidden';
      iframe.style.width = '0px';
      iframe.title = 'Threads';
      document.body.appendChild(iframe);
    }

    const updatePosition = () => {
      const anchor = document.getElementById(THREADS_VIEWPORT_ANCHOR_ID);

      if (!anchor || !iframe) {
        return;
      }

      const rect = anchor.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      iframe.style.height = `${Math.round(rect.height)}px`;
      iframe.style.left = `${Math.round(rect.left)}px`;
      iframe.style.top = `${Math.round(rect.top)}px`;
      iframe.style.width = `${Math.round(rect.width)}px`;
    };

    const show = () => {
      if (!iframe) {
        return;
      }

      iframe.style.pointerEvents = 'auto';
      iframe.style.visibility = 'visible';
      updatePosition();
    };

    const hide = () => {
      if (!iframe) {
        return;
      }

      iframe.style.pointerEvents = 'none';
      iframe.style.visibility = 'hidden';
    };

    const onScrollOrResize = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(updatePosition);
    };

    const ensureObserved = () => {
      const anchor = document.getElementById(THREADS_VIEWPORT_ANCHOR_ID);

      if (!anchor || resizeObserverRef.current) {
        return;
      }

      resizeObserverRef.current = new ResizeObserver(updatePosition);
      resizeObserverRef.current.observe(anchor);
    };

    if (location.pathname === '/threads') {
      show();
      ensureObserved();
      window.addEventListener('resize', onScrollOrResize, true);
      window.addEventListener('scroll', onScrollOrResize, true);
      updatePosition();
      setTimeout(updatePosition, 0);
      setTimeout(updatePosition, 200);
    } else {
      hide();

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      window.removeEventListener('resize', onScrollOrResize, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      window.removeEventListener('resize', onScrollOrResize, true);
      window.removeEventListener('scroll', onScrollOrResize, true);

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [location.pathname]);

  return null;
};
